import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import styled from '@emotion/styled';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import DraggableBlockItemComponent, { renderBlockPreviewContent } from './DraggableBlockItem'; // 방금 만든 컴포넌트 임포트
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import { FileText, Minimize2 } from 'lucide-react';
import { Table2 } from 'lucide-react';
import { Code } from 'lucide-react';
import { HelpCircle } from 'lucide-react';
import { Upload } from 'lucide-react';
import { Image } from 'lucide-react';
import { Layout } from 'lucide-react';
// 임시 스타일 정의
const TempBlockEditorStyles = {
    InlineBlockContainer: styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    margin-top: 20px;
  `,
    BlockDropZone: styled.div`
    height: ${(props)=>props.$isActive ? '80px' : '20px'};
    margin: 4px 0;
    border-radius: 4px;
    background-color: ${(props)=>props.$isActive ? 'rgba(37, 99, 235, 0.1)' : '#f3f4f6'};
    border: ${(props)=>props.$isActive ? '2px dashed #2563eb' : '1px dashed #d1d5db'};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    &:hover { height: 36px; background-color: rgba(37, 99, 235, 0.05); border: 2px dashed #93c5fd; }
    span { opacity: 0; transition: opacity 0.2s ease; }
    &:hover span { opacity: 1; }
  `,
    BlockTypeDropdown: styled.div`
    position: absolute;
    ${(props)=>props.$position === 'top' ? 'bottom: 100%;' : 'top: 0;'}
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 16px;
    min-width: 500px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 12px;
    margin: ${(props)=>props.$position === 'top' ? '0 0 8px 0' : '8px 0 0 0'};
  `,
    HorizontalMenuContainer: styled.div`width: 100%; display: flex; flex-direction: column;`,
    MenuHeader: styled.div`margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb; text-align: center; width: 100%;`,
    MenuButtonGroup: styled.div`display: flex; flex-direction: row; justify-content: center; gap: 12px; flex-wrap: wrap;`,
    BlockMenuButton: styled.button`
    display: flex; align-items: center; gap: 6px; padding: 8px 12px; border-radius: 4px; 
    border: 1px solid #e5e7eb; background-color: white; color: #4b5563; font-size: 0.9rem; 
    cursor: pointer; transition: all 0.2s; flex: 0 0 auto; min-width: 120px; text-align: center; justify-content: center;
    &:hover { background-color: #f3f4f6; border-color: #d1d5db; }
  `
};
const ExpandedOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2100;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;
const ExpandedContainer = styled.div`
  width: min(100%, 980px);
  max-height: 92vh;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 38px 60px rgba(15, 23, 42, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
const ExpandedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
`;
const ExpandedTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 10px;
`;
const ExpandedCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #d7e3f4;
  background: #ffffff;
  color: #1f2937;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5fc;
    border-color: #b9c7ea;
  }
`;
const ExpandedBody = styled.div`
  padding: 24px 26px;
  overflow-y: auto;
  background: #ffffff;
`;
const BlockEditorModeComponent = /*#__PURE__*/ React.memo(({ blocks, activeDropZone, dropdownPosition, editorOptions, onDragEnd, onBlockDragOver, onDragLeave, onBlockDrop, onOpenAddMenu, onCloseAddMenu, onAddBlockAt, onBlockContentChange, onToggleBlockOpen, onRemoveBlock, onBlockEditorMount, getBlockTypeIcon, getBlockTypeLabel, getBlockSummary })=>{
    // 임시 스타일 사용
    const { InlineBlockContainer, BlockDropZone, BlockTypeDropdown, HorizontalMenuContainer, MenuHeader, MenuButtonGroup, BlockMenuButton } = TempBlockEditorStyles;
    const MIN_EDITOR_HEIGHT = 200; // 필요 시 props로 받거나 여기서 정의
    const [expandedBlockId, setExpandedBlockId] = React.useState(null);
    const expandedBlock = React.useMemo(()=>blocks.find((b)=>b.id === expandedBlockId) || null, [
        blocks,
        expandedBlockId
    ]);
    const closeExpandedPreview = ()=>setExpandedBlockId(null);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(InlineBlockContainer, {
                children: /*#__PURE__*/ _jsx(DragDropContext, {
                    onDragEnd: onDragEnd,
                    children: /*#__PURE__*/ _jsx(Droppable, {
                        droppableId: "blocks",
                        children: (provided)=>/*#__PURE__*/ _jsxs("div", {
                                ...provided.droppableProps,
                                ref: provided.innerRef,
                                children: [
                                    /*#__PURE__*/ _jsx(BlockDropZone, {
                                        $isActive: activeDropZone === 0,
                                        onDragOver: (e)=>onBlockDragOver(e, 0),
                                        onDragLeave: onDragLeave,
                                        onDrop: (e)=>onBlockDrop(e, 0),
                                        onClick: (e)=>onOpenAddMenu(0, e),
                                        style: {
                                            height: '36px',
                                            marginBottom: '16px'
                                        },
                                        children: /*#__PURE__*/ _jsx("span", {
                                            style: {
                                                opacity: 1
                                            },
                                            children: "+ 새 블록 추가"
                                        })
                                    }),
                                    blocks.map((block, index)=>/*#__PURE__*/ _jsxs(React.Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(DraggableBlockItemComponent // 분리된 컴포넌트 사용
                                                , {
                                                    block: block,
                                                    index: index,
                                                    editorOptions: editorOptions,
                                                    onContentChange: onBlockContentChange,
                                                    onToggleOpen: onToggleBlockOpen,
                                                    onRemove: onRemoveBlock,
                                                    onEditorMount: onBlockEditorMount,
                                                    getBlockTypeIcon: getBlockTypeIcon,
                                                    getBlockTypeLabel: getBlockTypeLabel,
                                                    getBlockSummary: getBlockSummary,
                                                    onPreviewExpand: (selectedBlock)=>setExpandedBlockId(selectedBlock.id)
                                                }),
                                                /*#__PURE__*/ _jsx(BlockDropZone, {
                                                    $isActive: activeDropZone === index + 1,
                                                    onDragOver: (e)=>onBlockDragOver(e, index + 1),
                                                    onDragLeave: onDragLeave,
                                                    onDrop: (e)=>onBlockDrop(e, index + 1),
                                                    onClick: (e)=>onOpenAddMenu(index + 1, e),
                                                    children: /*#__PURE__*/ _jsx("span", {
                                                        children: "블록 추가"
                                                    })
                                                })
                                            ]
                                        }, block.id)),
                                    provided.placeholder,
                                    dropdownPosition && // Dropdown 메뉴 부분 (스타일링 및 onAddBlockAt 호출 수정)
                                    /*#__PURE__*/ _jsx("div", {
                                        id: "block-menu",
                                        style: {
                                            position: 'fixed',
                                            top: dropdownPosition.y,
                                            left: 0,
                                            right: 0,
                                            zIndex: 1000
                                        },
                                        children: /*#__PURE__*/ _jsx(BlockTypeDropdown, {
                                            $position: dropdownPosition.position,
                                            children: /*#__PURE__*/ _jsxs(HorizontalMenuContainer, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(MenuHeader, {
                                                        children: /*#__PURE__*/ _jsx("h5", {
                                                            style: {
                                                                margin: 0,
                                                                fontWeight: 500
                                                            },
                                                            children: "새 블록 추가"
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsxs(MenuButtonGroup, {
                                                        children: [
                                                            /*#__PURE__*/ _jsxs(BlockMenuButton, {
                                                                onClick: ()=>onAddBlockAt('markdown', dropdownPosition.index),
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(FileText, {
                                                                        size: 16,
                                                                        style: {
                                                                            marginRight: '6px'
                                                                        }
                                                                    }),
                                                                    " 마크다운"
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs(BlockMenuButton, {
                                                                onClick: ()=>onAddBlockAt('table', dropdownPosition.index),
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(Table2, {
                                                                        size: 16,
                                                                        style: {
                                                                            marginRight: '6px'
                                                                        }
                                                                    }),
                                                                    " 표"
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs(BlockMenuButton, {
                                                                onClick: ()=>onAddBlockAt('terminal', dropdownPosition.index),
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(Code, {
                                                                        size: 16,
                                                                        style: {
                                                                            marginRight: '6px'
                                                                        }
                                                                    }),
                                                                    " 터미널"
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs(BlockMenuButton, {
                                                                onClick: ()=>onAddBlockAt('quiz', dropdownPosition.index),
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(HelpCircle, {
                                                                        size: 16,
                                                                        style: {
                                                                            marginRight: '6px'
                                                                        }
                                                                    }),
                                                                    " 객관식 퀴즈"
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs(BlockMenuButton, {
                                                                onClick: ()=>{
                                                                    const zipPracticeContent = ':::quiz\ntype: code-practice\n\nzipUrl: "/practice/example.zip"\ntimeLimit: 30 # 분 단위\npracticeType: "실습"\nsetupInstructions: "실습 환경 설정 방법을 입력하세요."\nproblemDescription: "문제 설명을 입력하세요."\nrequirements: |\n  - 요구사항 1\n  - 요구사항 2\n:::';
                                                                    const index = dropdownPosition.index;
                                                                    onAddBlockAt('quiz', index, zipPracticeContent, {
                                                                        blockName: 'quiz',
                                                                        quizType: 'code-practice'
                                                                    });
                                                                },
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(Upload, {
                                                                        size: 16,
                                                                        style: {
                                                                            marginRight: '6px'
                                                                        }
                                                                    }),
                                                                    " ZIP 실습"
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs(BlockMenuButton, {
                                                                onClick: ()=>onAddBlockAt('image', dropdownPosition.index),
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(Image, {
                                                                        size: 16,
                                                                        style: {
                                                                            marginRight: '6px'
                                                                        }
                                                                    }),
                                                                    " 이미지"
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs(BlockMenuButton, {
                                                                onClick: ()=>onAddBlockAt('html', dropdownPosition.index),
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(Layout, {
                                                                        size: 16,
                                                                        style: {
                                                                            marginRight: '6px'
                                                                        }
                                                                    }),
                                                                    " HTML"
                                                                ]
                                                            })
                                                        ]
                                                    })
                                                ]
                                            })
                                        })
                                    })
                                ]
                            })
                    })
                })
            }),
            dropdownPosition && /*#__PURE__*/ _jsx("div", {
                style: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999
                },
                onClick: onCloseAddMenu
            }),
            expandedBlock && /*#__PURE__*/ _jsx(ExpandedOverlay, {
                role: "dialog",
                "aria-modal": "true",
                onClick: closeExpandedPreview,
                children: /*#__PURE__*/ _jsxs(ExpandedContainer, {
                    onClick: (e)=>e.stopPropagation(),
                    children: [
                        /*#__PURE__*/ _jsxs(ExpandedHeader, {
                            children: [
                                /*#__PURE__*/ _jsxs(ExpandedTitle, {
                                    children: [
                                        getBlockTypeIcon(expandedBlock.type, expandedBlock.metadata?.blockName),
                                        getBlockTypeLabel(expandedBlock.type),
                                        expandedBlock.metadata?.blockName && ` · ${expandedBlock.metadata.blockName}`
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(ExpandedCloseButton, {
                                    type: "button",
                                    onClick: closeExpandedPreview,
                                    "aria-label": "미리보기 닫기",
                                    children: /*#__PURE__*/ _jsx(Minimize2, {
                                        size: 16
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(ExpandedBody, {
                            children: renderBlockPreviewContent(expandedBlock)
                        })
                    ]
                })
            })
        ]
    });
});
export default BlockEditorModeComponent;
